/**
 * views/CustomerAccess.tsx — Operations Console › Warehouse Access
 * ────────────────────────────────────────────────────────────────
 * Admin-only module to control which warehouses each customer OR manager
 * account may view reports for.
 *
 * Flow
 * ────
 *  1. Pick a user from the dropdown (role = 'customer' or 'manager' accounts,
 *     grouped by role).
 *  2. Check the warehouses they are allowed to access.
 *  3. Save → PUT /api/admin/customer-access/customers/{id} replaces the set
 *     (the same endpoint accepts manager targets).
 *
 * A user with NO warehouses checked sees no reports at all (enforced
 * server-side in the inventory / reports routers).
 */

import { useEffect, useMemo, useState } from 'react';

import { apiClient, ApiError } from '@/api/client';

// ── Types (local to this admin module) ─────────────────────────────────────────

interface CustomerRow {
  id: string;
  email: string;
  name: string;
  company?: string | null;
  is_active: boolean;
  warehouse_ids: string[];
  /** Backend returns 'customer' or 'manager'; default to 'customer' for safety. */
  role?: string;
}

interface WarehouseOption {
  id: string;
  name: string;
  code: string;
  is_active: boolean;
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function CustomerAccess() {
  const [customers, setCustomers]   = useState<CustomerRow[] | null>(null);
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [loadError, setLoadError]   = useState<string | null>(null);

  const [selectedId, setSelectedId] = useState<string>('');
  const [checked, setChecked]       = useState<Set<string>>(new Set());

  const [busy, setBusy]       = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [success, setSuccess]     = useState<string | null>(null);

  const load = async () => {
    setLoadError(null);
    try {
      const [custs, whs] = await Promise.all([
        apiClient.get<CustomerRow[]>('/api/admin/customer-access/customers'),
        apiClient.get<WarehouseOption[]>('/api/admin/customer-access/warehouses'),
      ]);
      setCustomers(custs);
      setWarehouses(whs);
    } catch (err) {
      setLoadError(err instanceof ApiError ? err.message : 'Failed to load warehouse access data.');
    }
  };

  useEffect(() => { load(); }, []);

  const selected = useMemo(
    () => customers?.find(c => c.id === selectedId) ?? null,
    [customers, selectedId],
  );

  // Split the combined list into role groups for clearer assignment.
  const managerRows  = useMemo(() => (customers ?? []).filter(c => c.role === 'manager'), [customers]);
  const customerRows = useMemo(() => (customers ?? []).filter(c => c.role !== 'manager'), [customers]);

  // When the selected customer changes, seed the checkbox set from their grants.
  useEffect(() => {
    if (selected) setChecked(new Set(selected.warehouse_ids));
    else setChecked(new Set());
    setSuccess(null);
    setSaveError(null);
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (whId: string) => {
    setChecked(prev => {
      const next = new Set(prev);
      next.has(whId) ? next.delete(whId) : next.add(whId);
      return next;
    });
    setSuccess(null);
  };

  const save = async () => {
    if (!selectedId) return;
    setBusy(true); setSaveError(null); setSuccess(null);
    try {
      const updated = await apiClient.put<CustomerRow>(
        `/api/admin/customer-access/customers/${selectedId}`,
        { warehouse_ids: [...checked] },
      );
      // Reflect the saved set back into the in-memory list.
      setCustomers(prev =>
        prev ? prev.map(c => (c.id === updated.id ? updated : c)) : prev);
      setSuccess(
        `Saved — ${updated.name || updated.email} can now access ` +
        `${updated.warehouse_ids.length} warehouse${updated.warehouse_ids.length !== 1 ? 's' : ''}.`,
      );
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : 'Failed to save access.');
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="mgr-users-wrap">
        <div className="form-error">{loadError}</div>
        <button className="btn btn--ghost btn--sm" onClick={load}>↻ Retry</button>
      </div>
    );
  }

  if (customers === null) {
    return <div className="mgr-user-count">Loading user accounts…</div>;
  }

  const renderOption = (c: CustomerRow) => (
    <option key={c.id} value={c.id}>
      {c.name || c.email}{c.company ? ` · ${c.company}` : ''}
      {c.is_active ? '' : ' (disabled)'}
      {` · ${c.warehouse_ids.length} warehouse${c.warehouse_ids.length !== 1 ? 's' : ''}`}
    </option>
  );

  return (
    <div className="mgr-users-wrap">
      <div className="mgr-user-count">
        <b>{customerRows.length}</b> customer{customerRows.length !== 1 ? 's' : ''}
        {' · '}
        <b>{managerRows.length}</b> manager{managerRows.length !== 1 ? 's' : ''}
      </div>

      {customers.length === 0 ? (
        <div className="mgr-placeholder">
          <span className="mgr-placeholder-icon">👤</span>
          <p>No customer or manager accounts yet. Create one in the <b>Users</b> module first.</p>
        </div>
      ) : (
        <>
          {/* ── User picker (grouped by role) ── */}
          <div className="form-field">
            <label className="form-label">User account</label>
            <select
              className="form-input"
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              disabled={busy}
            >
              <option value="">— Select a user —</option>
              {customerRows.length > 0 && (
                <optgroup label="Customers">
                  {customerRows.map(renderOption)}
                </optgroup>
              )}
              {managerRows.length > 0 && (
                <optgroup label="Managers">
                  {managerRows.map(renderOption)}
                </optgroup>
              )}
            </select>
          </div>

          {/* ── Warehouse checklist ── */}
          {selected && (
            <div className="form-field">
              <label className="form-label">
                Authorised warehouses
                <span className="mgr-role-chip" style={{ marginLeft: 8 }}>
                  {selected.role === 'manager' ? 'manager' : 'customer'}
                </span>
              </label>
              {warehouses.length === 0 ? (
                <p className="aisle-picker-hint">No warehouses exist yet.</p>
              ) : (
                <div className="aisle-picker">
                  <div className="aisle-picker-grid">
                    {warehouses.map(w => (
                      <label
                        key={w.id}
                        className={`aisle-chip-label ${checked.has(w.id) ? 'aisle-chip-label--selected' : ''}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked.has(w.id)}
                          onChange={() => toggle(w.id)}
                          disabled={busy}
                        />
                        <span className="aisle-chip-name">{w.name} ({w.code})</span>
                        {!w.is_active && <span className="aisle-chip-count">inactive</span>}
                      </label>
                    ))}
                  </div>
                  <p className="aisle-picker-hint" style={{ marginTop: 8 }}>
                    Leave all unchecked = this user sees no reports.
                  </p>
                </div>
              )}
            </div>
          )}

          {saveError && <div className="form-error">{saveError}</div>}
          {success   && <div className="form-success">{success}</div>}

          {selected && (
            <button className="btn btn--primary" onClick={save} disabled={busy}>
              {busy ? 'Saving…' : 'Save access'}
            </button>
          )}
        </>
      )}
    </div>
  );
}
